<template>
  <div class="upload-container relative">
    <input
      v-if="!android"
      ref="input"
      type="file"
      class="custom-file-upload"
      :accept="accept"
      :aria-label="inputLabel"
      @change="trigger"
    />

    <button
      v-if="android"
      type="button"
      class="input"
      :aria-label="inputLabel"
      @click="takePicture"
    ></button>
    <div
      v-show="progress != 0"
      data-testid="upload-progress"
      class="absolute bottom-0 left-0.5 right-0.5"
    >
      <div class="relative">
        <div class="overflow-hidden h-1 text-xs flex rounded-lg rounded-t-none">
          <div
            :style="{ width: progress + '%' }"
            class="transition-width shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
          ></div>
        </div>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script>
import S3Uploader from "@/utils/s3-uploader"
import Api from "@/api"
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera"
import { Capacitor } from "@capacitor/core"

export default {
  name: "Upload",
  props: {
    value: {},
    accept: {
      type: String,
      default: "image/*",
    },
    inputLabel: {
      type: String,
      default: null,
    },
    storageKey: {
      type: String,
      default: "cache",
    },
  },
  data() {
    return {
      progress: 0,
      android: Capacitor.getPlatform() === "android",
    }
  },
  watch: {
    progress(value) {
      this.$emit("uploading", value > 0 && value < 100)
      if (value == 100) {
        this.progress = 0
        if (this.$refs.input) {
          this.$refs.input.value = null
        }
      }
    },
  },
  methods: {
    async takePicture() {
      const image_selection = await new Promise((resolve) => {
        const result = confirm(
          "Queres tirar uma foto nova? Carrega em Cancelar para escolher da galeria."
        )
        resolve(result ? "camera" : "Fotos")
      })

      const image = await Camera.getPhoto({
        source:
          image_selection === "camera"
            ? CameraSource.Camera
            : CameraSource.Photos,
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
      })

      const blobData = this.b64toBlob(
        image.base64String,
        `image/${image.format}`
      )
      const name = Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, "")
        .substr(0, 10)
      const file = new File([blobData], name, {
        type: `image/${image.format}`,
      })
      this.trigger({ target: { files: [file] } })
    },
    b64toBlob(b64Data, contentType = "", sliceSize = 512) {
      const byteCharacters = atob(b64Data)
      const byteArrays = []

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize)

        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
      }

      const blob = new Blob(byteArrays, { type: contentType })
      return blob
    },
    trigger(event) {
      const file = event.target.files[0]
      S3Uploader(file, this.onProgress, this.onError, {
        presign: Api.createUploadSignature,
        storage: this.storageKey,
      }).then((r) => {
        if (!r.url) {
          return
        }
        const data = {
          id: r.name,
          url: r.url,
          storage: r.storage,
          metadata: {
            filename: r.name,
            size: file.size,
            mime_type: file.type,
          },
        }
        this.$emit("input", data)
      }).catch(this.onError)
    },
    onProgress(value) {
      this.progress = value
    },
    onError(value) {
      this.progress = 0
      this.$emit("error", value)
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-file-upload,
.input {
  @apply block opacity-0 absolute inset-0 z-10 w-full h-full cursor-pointer;
}

.upload-container:focus-within {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.transition-width {
  transition: width 0.5s ease-in-out;
}
</style>
